import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserService } from './user.service';
import { UserApiError, UserApiErrorTypes } from './user.errors';
import {
  SetUserName,
  UpdateUserProfile,
  SetUserApiError,
  LoadOrganizationProfile,
  SetOrganizationProfile,
  UpdateOrganizationProfile,
  LoadOrganizationConfig,
  SetOrganizationConfig,
  SwitchOrganizationClicked,
  SwitchOrganizationSucceeded,
  UserAuthActions,
  UpdateOrganizationProfileSucceeded,
} from './user.actions';
import { mergeMap, map, catchError, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { WindowService } from 'src/app/shared/services/window.service';
import { AuthLegacyService } from './auth-legacy.service';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private authService: AuthLegacyService,
    private windowService: WindowService,
  ) {}

  switchOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchOrganizationClicked),
      switchMap(({ organizationId }) =>
        this.userService.setCurrentOrganization(organizationId).pipe(
          map(() => SwitchOrganizationSucceeded()),
          catchError((error) =>
            of(
              SetUserApiError({
                error: new UserApiError(UserApiErrorTypes.SwitchOrganizationError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  reloadPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SwitchOrganizationSucceeded),
        tap(() => {
          this.windowService.reload();
        }),
      ),
    { dispatch: false },
  );

  loadOrganizationConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadOrganizationConfig),
      switchMap(() =>
        this.userService.getOrganizationConfig().pipe(
          map((organizationConfig) => SetOrganizationConfig({ organizationConfig })),
          catchError((error) =>
            of(
              SetUserApiError({
                error: new UserApiError(UserApiErrorTypes.LoadOrganizationConfigError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateUserProfile),
      mergeMap((action) =>
        this.userService.updateProfile(action.userProfile).pipe(
          map(({ family_name, given_name }) =>
            SetUserName({ familyName: family_name, givenName: given_name }),
          ),
          catchError((error) =>
            of(
              SetUserApiError({
                error: new UserApiError(UserApiErrorTypes.UpdateProfileError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadOrganizationProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadOrganizationProfile),
      mergeMap(() =>
        this.userService.getOrganizationProfile().pipe(
          map((organizationProfile) => SetOrganizationProfile({ organizationProfile })),
          catchError((error) =>
            of(
              SetUserApiError({
                error: new UserApiError(UserApiErrorTypes.LoadOrganizationProfileError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateOrganizationProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganizationProfile),
      mergeMap(({ organizationProfile }) =>
        this.userService.updateOrganizationProfile(organizationProfile).pipe(
          switchMap(() => [
            SetOrganizationProfile({ organizationProfile }),
            UpdateOrganizationProfileSucceeded({ organizationProfile }),
          ]),
          catchError((error) =>
            of(
              SetUserApiError({
                error: new UserApiError(UserApiErrorTypes.UpdateOrganizationProfileError, error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public refreshUserTokenAfterOrgProfileUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganizationProfileSucceeded),
      switchMap(() =>
        this.authService.refreshUserToken().pipe(
          map(() => UserAuthActions.refreshUserTokenSucceeded()),
          catchError((error) => of(UserAuthActions.refreshUserTokenErrored({ error }))),
        ),
      ),
    ),
  );
}
